import { Injectable, Inject } from '@angular/core';
import { IdentityService } from 'src/app/core/identity.service';
import { ToasterService } from './toaster.service';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { LDFeatureManager } from './ld-feature-manager';
import { FeatureFlag } from '../models/enums/feature-flag';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WINDOW } from 'src/app/core/core.module';
import { take } from 'rxjs';

const ALL_FACILITIES = 'AllFacilities';
export interface UDOnlyLinkSecurityDto {
    novaUser: string;
    link: string;
}
@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class UnitDescriptionSecureWindowService {
    accessSecureUDLinks: boolean;
    accessSecureUDLinksFacilities = [];
    isLotusOneUrlReference = false;
    constructor(
        private readonly toasterService: ToasterService,
        private readonly identityService: IdentityService,
        @Inject(WINDOW) private readonly window: Window,
        @Inject(APP_CONFIG) private readonly _settings: Settings,
        private readonly _lDFeatureManager: LDFeatureManager,
        private readonly _identityService: IdentityService
    ) {
        this._lDFeatureManager
            .isEnabled(FeatureFlag.AccessSecureUDLinks)
            .pipe(untilDestroyed(this))
            .subscribe((value) => {
                this.accessSecureUDLinks = value;
            });

        this._lDFeatureManager
            .getJsonFeatureFlag(FeatureFlag.AccessSecureUdLinksByFacilities)
            .pipe(untilDestroyed(this))
            .subscribe((value: any[]) => {
                this.accessSecureUDLinksFacilities = value;
            });

        this._lDFeatureManager
            .isEnabled(FeatureFlag.LotusOneUrlReferencesNova)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                this.isLotusOneUrlReference = flagIsEnabled;
            });
    }

    openSecureWindow(url: string, target: string, isEdit: boolean, token: string): void {
        let newWindow = undefined;
        isEdit = !!isEdit; //This ensures isEdit is always a primitive boolean

        if (isEdit === true) {
            newWindow = this.window.open(`${url}`, target);
        } else {
            newWindow = this.window.open(`${url}&token=${token}`, target);
        }

        if (!newWindow) {
            this.toasterService.toastErrors('Failed to open new window');
            return;
        }

        const interval: number = window.setInterval(() => {
            if (newWindow.closed) {
                clearInterval(interval);
                return;
            }
            newWindow.postMessage({ type: 'PING' }, `https://${this.getBaseUrl()}`);
        }, 100);

        window.addEventListener('message', (event) => this.handlePostMessage(event, newWindow, interval));
    }

    handlePostMessage(event: MessageEvent, newWindow: Window | null, interval: number): void {
        if (event.origin !== `https://${this.getBaseUrl()}`) {
            return;
        }
        if (event.data && event.data.type === 'PONG') {
            clearInterval(interval);
            this.sendAccessToken(newWindow);
        }
    }

    sendAccessToken(newWindow: Window | null): void {
        if (!newWindow) return;

        this._identityService.coreAccessToken$.pipe(take(1)).subscribe((accessToken) => {
            if (accessToken) {
                newWindow.postMessage({ nova_access_token: accessToken }, `https://${this.getBaseUrl()}`);
            }
        });
    }

    getBaseUrl(): string {
        let urlBase;
        urlBase = this._settings.ayacDomain.split('//');
        if (this.isLotusOneUrlReference) {
            urlBase = this._settings.lotusOneDomain.split('//');
        }
        return urlBase[urlBase.length - 1];
    }

    open(url: string, target: string, isEdit: boolean, facilityId?: number): void {
        if (this.accessSecureUDLinks) {
            this.openSecureWindow(url, target, isEdit, '');
        } else {
            this.window.open(url, target);
        }
    }

    isAccessSecureUdLinksByFacilities(facilityId: number): boolean {
        if (typeof this.accessSecureUDLinksFacilities.length === 'undefined') {
            return false;
        } else if (
            this.accessSecureUDLinksFacilities.length === 1 &&
            String(this.accessSecureUDLinksFacilities[0]) === ALL_FACILITIES
        ) {
            return true;
        } else {
            return this.accessSecureUDLinksFacilities.includes(facilityId);
        }
    }
}
